/**
 * ZXing decoding over a plain grey buffer.
 *
 * The camera scanner lets @zxing/browser own a <video>; the software
 * digital-zoom pass has no element to hand over — it produces pixels. This
 * wraps the reader for that case: one reader per scanning session, reused
 * across every frame, with the same format whitelist the live scanner uses.
 */
import {
    BinaryBitmap,
    DecodeHintType,
    HybridBinarizer,
    MultiFormatReader,
    RGBLuminanceSource,
} from '@zxing/library'
import { BARCODE_FORMATS_1D_AND_QR } from './barcode-camera'

/** Decodes one grey frame, returning the text or null when nothing is found. */
export type DecodeLuminance = (
    luminance: Uint8ClampedArray,
    width: number,
    height: number,
) => string | null

export function createZxingLuminanceDecoder(): DecodeLuminance {
    const hints = new Map<DecodeHintType, unknown>([
        [DecodeHintType.POSSIBLE_FORMATS, BARCODE_FORMATS_1D_AND_QR],
        [DecodeHintType.TRY_HARDER, true],
    ])
    const reader = new MultiFormatReader()
    reader.setHints(hints)

    return (luminance, width, height) => {
        const source = new RGBLuminanceSource(luminance, width, height)
        try {
            const result = reader.decode(
                new BinaryBitmap(new HybridBinarizer(source)),
            )
            return result.getText()
        } catch {
            // No code in this crop — the normal outcome for most frames.
            return null
        } finally {
            // Leave the reader ready for the next frame of the same session.
            reader.reset()
        }
    }
}
